/**
 * Whether a URL is safe to put in an `href`.
 *
 * Feed data is not our data. An RSS `<link>` is whatever the publisher wrote,
 * and `feedPreview.php` renders feeds the viewer has not vetted, so a link
 * reaching an anchor unchecked is a click away from `javascript:` or
 * `data:text/html,...` running on dlvr.it's own origin, with the viewer's
 * session. React escapes the TEXT it renders; it does not sanitise this.
 *
 * An allowlist, not a blocklist: the dangerous set is open-ended (`vbscript:`,
 * `blob:`, whatever a browser ships next) while the set a feed item legitimately
 * needs is three entries long.
 */

/**
 * Relative and protocol-relative URLs need no entry — they resolve against
 * dlvr.it, which is where the page already is.
 */
const ALLOWED_SCHEMES = ["http:", "https:", "mailto:"]

export function safeHref(url: string | null | undefined): string | undefined {
  if (!url) return undefined

  /*
   * Control characters and whitespace come out before the scheme is read,
   * because browsers strip them too: a tab in the middle of "java<TAB>script:"
   * and a space in front of "javascript:" both still navigate. Reading the
   * scheme off the raw string would wave both through as "no scheme I
   * recognise, so it must be a path".
   */
  const cleaned = url.replace(/[\u0000-\u0020]/g, "")

  if (!cleaned) return undefined

  /*
   * A colon in the first segment is a scheme; a colon after the first `/`, `?`
   * or `#` is not. "example.com/a:b" has a colon and no scheme, and "/a?x=1:2"
   * is plainly relative — neither should be judged on it.
   */
  const scheme = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(cleaned.split(/[/?#]/)[0])

  if (!scheme) return url

  return ALLOWED_SCHEMES.includes(`${scheme[1].toLowerCase()}:`) ? url : undefined
}
